"use client";

export type RazorpaySdkKind = "checkout" | "custom";

export type RazorpayScriptLoadResult =
  | { ok: true; data: { loaded: true } }
  | { ok: false; message: string };

type LoadRazorpaySdkScriptOptions = {
  src: string;
  sdk: RazorpaySdkKind;
  statusAttribute: string;
  isReady: () => boolean;
  forceReload?: boolean;
  timeoutMs?: number;
  readyTimeoutMs?: number;
  attempts?: number;
  retryDelayMs?: number;
  failureMessage?: string;
  browserMessage?: string;
};

declare global {
  interface Window {
    __hiiveRazorpaySdk?: RazorpaySdkKind;
  }
}

const DEFAULT_SCRIPT_TIMEOUT_MS = 15000;
const DEFAULT_READY_TIMEOUT_MS = 1000;
const DEFAULT_ATTEMPTS = 3;
const DEFAULT_RETRY_DELAY_MS = 300;
const DEFAULT_FAILURE_MESSAGE = "Failed to load payment provider.";

const sleep = (ms: number) =>
  new Promise<void>((resolve) => window.setTimeout(resolve, ms));

const scriptUrlKey = (src: string) => {
  const url = new URL(src, window.location.href);
  return `${url.origin}${url.pathname}`;
};

const getMatchingScripts = (src: string) => {
  const expected = scriptUrlKey(src);
  return Array.from(document.scripts).filter((script) => {
    const scriptSrc = script.getAttribute("src");
    if (!scriptSrc) return false;
    return scriptUrlKey(script.src) === expected;
  });
};

const removeMatchingScripts = (src: string) => {
  for (const script of getMatchingScripts(src)) {
    script.remove();
  }
};

const waitForReady = async (isReady: () => boolean, timeoutMs: number) => {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() <= deadline) {
    if (isReady()) return true;
    await sleep(25);
  }
  return isReady();
};

const waitForScript = ({
  script,
  sdk,
  statusAttribute,
  isReady,
  timeoutMs,
  readyTimeoutMs,
  failureMessage,
}: {
  script: HTMLScriptElement;
  sdk: RazorpaySdkKind;
  statusAttribute: string;
  isReady: () => boolean;
  timeoutMs: number;
  readyTimeoutMs: number;
  failureMessage: string;
}) =>
  new Promise<void>((resolve, reject) => {
    let settled = false;
    let timeout: number | undefined;

    const cleanup = () => {
      if (timeout !== undefined) {
        window.clearTimeout(timeout);
      }
      script.removeEventListener("load", onLoad);
      script.removeEventListener("error", onError);
    };

    const fail = (message = failureMessage) => {
      if (settled) return;
      settled = true;
      cleanup();
      script.setAttribute(statusAttribute, "error");
      if (window.__hiiveRazorpaySdk === sdk) {
        window.__hiiveRazorpaySdk = undefined;
      }
      reject(new Error(message));
    };

    const succeed = async () => {
      if (settled) return;
      const ready = await waitForReady(isReady, readyTimeoutMs);
      if (!ready) {
        fail("Payment provider did not initialize.");
        return;
      }

      settled = true;
      cleanup();
      script.setAttribute(statusAttribute, "loaded");
      window.__hiiveRazorpaySdk = sdk;
      resolve();
    };

    function onLoad() {
      void succeed();
    }

    function onError() {
      fail();
    }

    timeout = window.setTimeout(() => fail(), timeoutMs);
    script.addEventListener("load", onLoad, { once: true });
    script.addEventListener("error", onError, { once: true });
    if (isReady() && window.__hiiveRazorpaySdk === sdk) {
      void succeed();
    }
  });

const loadScriptAttempt = async ({
  src,
  sdk,
  statusAttribute,
  isReady,
  forceReload,
  timeoutMs,
  readyTimeoutMs,
  failureMessage,
}: Required<
  Pick<
    LoadRazorpaySdkScriptOptions,
    | "src"
    | "sdk"
    | "statusAttribute"
    | "isReady"
    | "forceReload"
    | "timeoutMs"
    | "readyTimeoutMs"
    | "failureMessage"
  >
>) => {
  if (forceReload) {
    removeMatchingScripts(src);
  }

  const existing = getMatchingScripts(src).at(-1);
  if (existing) {
    const status = existing.getAttribute(statusAttribute);
    const ownsGlobal = window.__hiiveRazorpaySdk === sdk;

    if (status === "loaded" && ownsGlobal && isReady()) {
      window.__hiiveRazorpaySdk = sdk;
      return;
    }

    if (status === "loading") {
      await waitForScript({
        script: existing,
        sdk,
        statusAttribute,
        isReady,
        timeoutMs,
        readyTimeoutMs,
        failureMessage,
      });
      return;
    }

    removeMatchingScripts(src);
  }

  const script = document.createElement("script");
  script.src = src;
  script.async = true;
  script.setAttribute(statusAttribute, "loading");
  const scriptLoad = waitForScript({
    script,
    sdk,
    statusAttribute,
    isReady,
    timeoutMs,
    readyTimeoutMs,
    failureMessage,
  });
  document.head.appendChild(script);

  await scriptLoad;
};

export async function loadRazorpaySdkScript({
  src,
  sdk,
  statusAttribute,
  isReady,
  forceReload = false,
  timeoutMs = DEFAULT_SCRIPT_TIMEOUT_MS,
  readyTimeoutMs = DEFAULT_READY_TIMEOUT_MS,
  attempts = DEFAULT_ATTEMPTS,
  retryDelayMs = DEFAULT_RETRY_DELAY_MS,
  failureMessage = DEFAULT_FAILURE_MESSAGE,
  browserMessage = "Payments can only be loaded in the browser.",
}: LoadRazorpaySdkScriptOptions): Promise<RazorpayScriptLoadResult> {
  if (typeof window === "undefined") {
    return {
      ok: false,
      message: browserMessage,
    };
  }

  let lastMessage = failureMessage;
  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    try {
      await loadScriptAttempt({
        src,
        sdk,
        statusAttribute,
        isReady,
        forceReload: forceReload || attempt > 1,
        timeoutMs,
        readyTimeoutMs,
        failureMessage,
      });
      return { ok: true, data: { loaded: true } };
    } catch (error) {
      lastMessage =
        error instanceof Error && error.message.trim().length > 0
          ? error.message
          : failureMessage;
      removeMatchingScripts(src);
      if (window.__hiiveRazorpaySdk === sdk) {
        window.__hiiveRazorpaySdk = undefined;
      }
      if (attempt < attempts) {
        await sleep(retryDelayMs);
      }
    }
  }

  return { ok: false, message: lastMessage };
}
